import { BaseService } from './base-service';
import { GetErgonomicWorkAnalysisMapper } from '@/core/mappers/ergonomic-work-analysis/get-ergonomic-work-analysis';
import { GetErgonomicWorkAnalysisImagesMapper } from '@/core/mappers/ergonomic-work-analysis/get-ergonomic-work-analysis-images';
import type { ErgonomicWorkAnalysis } from '@/types/ErgonomicWorkAnalysis';
import type {
	GetActionPlanRequestDTO,
	GetActionPlanResponseDTO,
	GetErgonomicWorkAnalysisRequestDTO,
	GetErgonomicWorkAnalysisResponseDTO,
	CreateErgonomicWorkAnalysisRequestDTO,
	CreateErgonomicWorkAnalysisResponseDTO,
	UpdateErgonomicWorkAnalysisRequestDTO,
	UpdateErgonomicWorkAnalysisResponseDTO,
	GetErgonomicWorkAnalysisListResponseDTO,
	GetErgonomicWorkAnalysisListRequestDTO,
	DeleteErgonomicWorkAnalysisRequestDTO,
	DeleteErgonomicWorkAnalysisResponseDTO,
	GetErgonomicWorkAnalysisReportDownloadRequestDTO,
	GetErgonomicWorkAnalysisReportDownloadResponseDTO,
	GetSelectedCustomReportResultsRequestDTO,
	GetSelectedCustomReportResultsResponseDTO,
	GeneratedDocumentErgonomicWorkAnalysisRequestDTO,
	GeneratedDocumentErgonomicWorkAnalysisResponseDTO,
	GetEwaStepsWithMaximumResultRequestDTO,
	GetEwaStepKeyWithMaximumResultResponseDTO,
	CreateErgonomicWorkAnalysisUrlSignedRequestDTO,
	CreateErgonomicWorkAnalysisUrlSignedResponseDTO,
	UpdateErgonomicWorkAnalysisImageDescriptionRequestDTO,
	UpdateErgonomicWorkAnalysisImageDescriptionResponseDTO,
	DeleteErgonomicWorkAnalysisImageRequestDTO,
	DeleteErgonomicWorkAnalysisImageResponseDTO,
	GetErgonomicWorkAnalysisImagesRequestDTO,
	GetErgonomicWorkAnalysisImagesResponseDTO,
	GetErgonomicWorkAnalysisImagesTransformedDTO
} from '@/core/dto/ergonomic-work-analisys';

export class ErgonomicWorkAnalysisService extends BaseService {
	private static instance: ErgonomicWorkAnalysisService;

	private constructor(public readonly basePath: string = '/ergonomic-work-analysis') {
		super();
	}

	public static getInstance(): ErgonomicWorkAnalysisService {
		if (!ErgonomicWorkAnalysisService.instance) {
			ErgonomicWorkAnalysisService.instance = new ErgonomicWorkAnalysisService();
		}
		return ErgonomicWorkAnalysisService.instance;
	}

	public async create(
		params: CreateErgonomicWorkAnalysisRequestDTO
	): Promise<CreateErgonomicWorkAnalysisResponseDTO> {
		const { data } = await this.getInstance().post<CreateErgonomicWorkAnalysisResponseDTO>(this.basePath, params);
		return data;
	}

	public async findOne(params: GetErgonomicWorkAnalysisRequestDTO): Promise<ErgonomicWorkAnalysis> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await this.getInstance().get<GetErgonomicWorkAnalysisResponseDTO>(url, { params });
		return new GetErgonomicWorkAnalysisMapper(data).toDomain();
	}

	public async update(
		params: UpdateErgonomicWorkAnalysisRequestDTO
	): Promise<UpdateErgonomicWorkAnalysisResponseDTO> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await this.getInstance().put<UpdateErgonomicWorkAnalysisResponseDTO>(url, params);
		return data;
	}

	public async findAll(
		params: GetErgonomicWorkAnalysisListRequestDTO
	): Promise<GetErgonomicWorkAnalysisListResponseDTO> {
		const { data } = await this.getInstance().get<GetErgonomicWorkAnalysisListResponseDTO>(this.basePath, {
			params
		});
		return data;
	}

	public async getStepsWithMaximumResult(
		params: GetEwaStepsWithMaximumResultRequestDTO
	): Promise<GetEwaStepKeyWithMaximumResultResponseDTO> {
		const url = this.basePath + `/steps-with-maximum-result`;
		const { data } = await this.getInstance().get<GetEwaStepKeyWithMaximumResultResponseDTO>(url, { params });
		return data;
	}

	public async delete(
		params: DeleteErgonomicWorkAnalysisRequestDTO
	): Promise<DeleteErgonomicWorkAnalysisResponseDTO> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await this.getInstance().delete<DeleteErgonomicWorkAnalysisResponseDTO>(url, {
			params
		});
		return data;
	}

	public async getSelectedCustomReportResults(
		params: GetSelectedCustomReportResultsRequestDTO
	): Promise<GetSelectedCustomReportResultsResponseDTO> {
		const url = this.basePath + `/${params.id}/custom-reports`;
		const { data } = await this.getInstance().get<GetSelectedCustomReportResultsResponseDTO>(url, { params });
		return data;
	}

	public async generateDocument(params: GeneratedDocumentErgonomicWorkAnalysisRequestDTO): Promise<{ url: string }> {
		const url = this.basePath + `/${params.id}/generate-document`;
		const { data } = await this.getInstance().post<GeneratedDocumentErgonomicWorkAnalysisResponseDTO>(url, params);
		return data;
	}

	public async downloadReport(params: GetErgonomicWorkAnalysisReportDownloadRequestDTO): Promise<{ url: string }> {
		const { organization_id, company_id, id } = params;
		const url = this.basePath + `/${id}/download`;
		const { data } = await this.getInstance().get<GetErgonomicWorkAnalysisReportDownloadResponseDTO>(url, {
			params: { organization_id, company_id }
		});
		return data;
	}

	public async getAllActionPlans(params: GetActionPlanRequestDTO): Promise<GetActionPlanResponseDTO> {
		const { ergonomic_work_analysis_id } = params;
		const url = this.basePath + `/${ergonomic_work_analysis_id}/action-plans`;
		const { data } = await this.getInstance().get<GetActionPlanResponseDTO>(url, { params });
		return data;
	}

	public async getImages(
		params: GetErgonomicWorkAnalysisImagesRequestDTO
	): Promise<GetErgonomicWorkAnalysisImagesTransformedDTO> {
		const url = this.basePath + `/${params.id}/images`;
		const { data } = await this.getInstance().get<GetErgonomicWorkAnalysisImagesResponseDTO[]>(url, { params });
		return new GetErgonomicWorkAnalysisImagesMapper(data).toDomain();
	}

	public async updateImageDescription(
		params: UpdateErgonomicWorkAnalysisImageDescriptionRequestDTO
	): Promise<UpdateErgonomicWorkAnalysisImageDescriptionResponseDTO> {
		const { id, image_id, ...body } = params;
		const url = this.basePath + `/${id}/images/${image_id}`;
		const { data } = await this.getInstance().put<UpdateErgonomicWorkAnalysisImageDescriptionResponseDTO>(
			url,
			body
		);
		return data;
	}

	public async deleteImage(
		params: DeleteErgonomicWorkAnalysisImageRequestDTO
	): Promise<DeleteErgonomicWorkAnalysisImageResponseDTO> {
		const { id, image_id, ...queryParams } = params;
		const url = this.basePath + `/${id}/images/${image_id}`;
		const { data } = await this.getInstance().delete<DeleteErgonomicWorkAnalysisImageResponseDTO>(url, {
			params: queryParams
		});
		return data;
	}

	public async createUploadUrlSigned(
		params: CreateErgonomicWorkAnalysisUrlSignedRequestDTO
	): Promise<CreateErgonomicWorkAnalysisUrlSignedResponseDTO> {
		const url = this.basePath + `/upload-url-signed/${params.id}`;
		const { data } = await this.getInstance().post<CreateErgonomicWorkAnalysisUrlSignedResponseDTO>(url, params);
		return data;
	}
}
